import React, { useState, useRef, useEffect, forwardRef } from 'react';

const TimeSelect = forwardRef(({ value, onChange, placeholder = 'Select time', label = '' }, ref) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const hourScrollRef = useRef(null);
  const minuteScrollRef = useRef(null);
  const periodScrollRef = useRef(null);

  // Parse value to get hour, minute, period
  const parseValue = (val) => {
    if (!val) return { hour: 9, minute: 0, period: 'AM' };
    const [hh, mm] = val.split(':');
    const h = parseInt(hh);
    const m = parseInt(mm);
    return {
      hour: h === 0 ? 12 : h > 12 ? h - 12 : h,
      minute: m,
      period: h >= 12 ? 'PM' : 'AM'
    };
  };

  const parsed = parseValue(value);
  const [selectedHour, setSelectedHour] = useState(parsed.hour);
  const [selectedMinute, setSelectedMinute] = useState(parsed.minute);
  const [selectedPeriod, setSelectedPeriod] = useState(parsed.period);
  const [tempHour, setTempHour] = useState(parsed.hour);
  const [tempMinute, setTempMinute] = useState(parsed.minute);
  const [tempPeriod, setTempPeriod] = useState(parsed.period);

  useEffect(() => {
    const p = parseValue(value);
    setSelectedHour(p.hour);
    setSelectedMinute(p.minute);
    setSelectedPeriod(p.period);
    setTempHour(p.hour);
    setTempMinute(p.minute);
    setTempPeriod(p.period);
  }, [value]);

  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Auto-scroll to selected values when modal opens
  useEffect(() => {
    if (open) {
      const scrollToCenter = (scrollRef, index, itemHeight = 40) => {
        if (scrollRef.current) {
          setTimeout(() => {
            scrollRef.current.scrollTop = Math.max(0, index * itemHeight - 80);
          }, 50);
        }
      };
      scrollToCenter(hourScrollRef, tempHour - 1);
      scrollToCenter(minuteScrollRef, tempMinute);
      scrollToCenter(periodScrollRef, tempPeriod === 'AM' ? 0 : 1);
    }
  }, [open]);

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  const handleApply = () => {
    setSelectedHour(tempHour);
    setSelectedMinute(tempMinute);
    setSelectedPeriod(tempPeriod);

    let hour24 = tempHour;
    if (tempPeriod === 'PM' && tempHour !== 12) hour24 = tempHour + 12;
    if (tempPeriod === 'AM' && tempHour === 12) hour24 = 0;
    
    const hh = String(hour24).padStart(2, '0');
    const mm = String(tempMinute).padStart(2, '0');
    onChange && onChange(`${hh}:${mm}`);
    setOpen(false);
  };

  const handleCancel = () => {
    // Reset to last applied values
    setTempHour(selectedHour);
    setTempMinute(selectedMinute);
    setTempPeriod(selectedPeriod);
    setOpen(false);
  };

  const displayValue = value
    ? `${selectedHour}:${String(selectedMinute).padStart(2, '0')} ${selectedPeriod}`
    : '';

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        ref={ref}
        onClick={() => setOpen(!open)}
        className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-left bg-white hover:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors"
      >
        <span className={displayValue ? 'text-gray-900 font-medium' : 'text-gray-400'}>
          {displayValue || placeholder}
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 p-4 w-80">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
            <div>
              <h3 className="font-semibold text-gray-900">
                {label ? `Select ${label} Time` : 'Select Time'}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">Choose hour, minute, and period</p>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Time Display */}
          <div className="mb-4 p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border border-amber-100">
            <div className="text-center">
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-2">
                {label || 'Time'}
              </p>
              <p className="text-3xl font-bold text-gray-900">
                {tempHour}:<span className="text-amber-600">{String(tempMinute).padStart(2, '0')}</span> {tempPeriod}
              </p>
            </div>
          </div>

          {/* Time Picker Scrollers */}
          <div className="flex gap-2 mb-4 justify-center">
            {/* Hour Column */}
            <div className="flex flex-col items-center flex-1">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Hour</p>
              <div 
                ref={hourScrollRef}
                className="relative h-40 overflow-y-scroll scrollbar-hide snap-y snap-mandatory bg-gray-50 rounded-lg border border-gray-200"
              >
                {/* Top spacer */}
                <div className="h-16" />
                {hours.map(h => (
                  <div
                    key={h}
                    onClick={() => setTempHour(h)}
                    className={`flex items-center justify-center cursor-pointer select-none snap-center transition-all duration-150 py-2 ${
                      tempHour === h
                        ? 'text-white font-bold scale-110 bg-amber-500 rounded-md mx-1'
                        : 'text-gray-500 font-normal hover:text-gray-700 hover:bg-gray-100 rounded-md mx-1'
                    }`}
                    style={{ height: '40px' }}
                  >
                    {h}
                  </div>
                ))}
                {/* Bottom spacer */}
                <div className="h-16" />
              </div>
            </div>

            {/* Separator */}
            <div className="flex items-center justify-center mb-8 text-2xl font-bold text-gray-300">:</div>

            {/* Minute Column */}
            <div className="flex flex-col items-center flex-1">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Minute</p>
              <div 
                ref={minuteScrollRef}
                className="relative h-40 overflow-y-scroll scrollbar-hide snap-y snap-mandatory bg-gray-50 rounded-lg border border-gray-200"
              >
                {/* Top spacer */}
                <div className="h-16" />
                {minutes.map(m => (
                  <div
                    key={m}
                    onClick={() => setTempMinute(m)}
                    className={`flex items-center justify-center cursor-pointer select-none snap-center transition-all duration-150 py-2 ${
                      tempMinute === m
                        ? 'text-white font-bold scale-110 bg-amber-500 rounded-md mx-1'
                        : 'text-gray-500 font-normal hover:text-gray-700 hover:bg-gray-100 rounded-md mx-1'
                    }`}
                    style={{ height: '40px' }}
                  >
                    {String(m).padStart(2, '0')}
                  </div>
                ))}
                {/* Bottom spacer */}
                <div className="h-16" />
              </div>
            </div>

            {/* AM/PM Column */}
            <div className="flex flex-col items-center flex-1">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Period</p>
              <div 
                ref={periodScrollRef}
                className="relative h-40 overflow-y-scroll scrollbar-hide snap-y snap-mandatory bg-gray-50 rounded-lg border border-gray-200"
              >
                {/* Top spacer */}
                <div className="h-16" />
                {['AM', 'PM'].map(p => (
                  <div
                    key={p}
                    onClick={() => setTempPeriod(p)}
                    className={`flex items-center justify-center cursor-pointer select-none snap-center transition-all duration-150 py-2 font-semibold ${
                      tempPeriod === p
                        ? 'text-white scale-110 bg-amber-500 rounded-md mx-1'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md mx-1'
                    }`}
                    style={{ height: '40px' }}
                  >
                    {p}
                  </div>
                ))}
                {/* Bottom spacer */}
                <div className="h-16" />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="flex-1 px-4 py-2 rounded-lg bg-amber-500 text-white font-medium hover:bg-amber-600 transition-colors shadow-md hover:shadow-lg"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

TimeSelect.displayName = 'TimeSelect';

export default TimeSelect;
